"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.eligibilityRuleInputSchema = exports.ELIGIBILITY_REASON_CODE_MAX_LEN = exports.ELIGIBILITY_RULE_NAME_MAX_LEN = exports.eligibilityRuleConditionInputSchema = void 0;
const zod_1 = require("zod");
const vocabulary_1 = require("./vocabulary");
const numericString = zod_1.z.string().regex(/^-?\d+(?:\.\d+)?$/, 'must be a number');
const isoDate = zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'must be ISO date YYYY-MM-DD');
// fact_kind is resolved server-side from the eligibility_fact registry. It is never trusted from client input.
const baseConditionShape = {
    fact_name: zod_1.z.string().min(1).max(120),
    fact_kind: vocabulary_1.eligibilityFactKindSchema,
    role: vocabulary_1.eligibilityConditionRoleSchema.default('requirement'),
    operator: vocabulary_1.eligibilityOperatorSchema,
    position: zod_1.z.number().int().min(0).optional(),
    value_text: zod_1.z.string().nullable().optional(),
    value_number: numericString.nullable().optional(),
    value_boolean: zod_1.z.boolean().nullable().optional(),
    value_date: isoDate.nullable().optional(),
    value_text_array: zod_1.z.array(zod_1.z.string()).default([]),
    value_number_array: zod_1.z.array(numericString).default([]),
    // value_date_array does not exist. The date kind has no in or not_in operator, so no array field is needed.
};
function summariseConditionShape(cond) {
    const scalars = [cond.value_text, cond.value_number, cond.value_boolean, cond.value_date];
    const arrays = [cond.value_text_array, cond.value_number_array];
    return {
        filledScalars: scalars.filter((v) => v !== null && v !== undefined).length,
        filledArrays: arrays.filter((v) => v.length > 0).length,
    };
}
function refineConditionShape(cond, ctx) {
    const op = cond.operator;
    const addIssue = (message) => {
        ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, message, path: ['operator'] });
    };
    if (!(0, vocabulary_1.isEligibilityOperatorSupportedForKind)(op, cond.fact_kind)) {
        addIssue(`operator '${op}' is not legal for fact kind '${cond.fact_kind}'`);
        return;
    }
    const { filledScalars, filledArrays } = summariseConditionShape(cond);
    if ((0, vocabulary_1.isEligibilityNullOperator)(op)) {
        if (filledScalars > 0 || filledArrays > 0) {
            addIssue(`operator '${op}' must not carry any value`);
        }
        return;
    }
    if ((0, vocabulary_1.isEligibilityListOperator)(op)) {
        if (filledScalars > 0)
            addIssue(`operator '${op}' must use an array, not a scalar value`);
        if (filledArrays !== 1)
            addIssue(`operator '${op}' requires exactly one non-empty value_*_array`);
        return;
    }
    if (filledArrays > 0)
        addIssue(`operator '${op}' must use a scalar value, not an array`);
    if (filledScalars !== 1)
        addIssue(`operator '${op}' requires exactly one non-null scalar value`);
}
const rawConditionSchema = zod_1.z.object(baseConditionShape).strict();
exports.eligibilityRuleConditionInputSchema = rawConditionSchema.superRefine(refineConditionShape);
exports.ELIGIBILITY_RULE_NAME_MAX_LEN = 80;
exports.ELIGIBILITY_REASON_CODE_MAX_LEN = 120;
const optionalTrimmedText = (max) => zod_1.z
    .string()
    .trim()
    .max(max)
    .nullable()
    .optional()
    .transform((v) => (v === undefined || v === null || v.length === 0 ? null : v));
exports.eligibilityRuleInputSchema = zod_1.z
    .object({
    name: zod_1.z.string().trim().min(1).max(exports.ELIGIBILITY_RULE_NAME_MAX_LEN),
    description: optionalTrimmedText(2000),
    reason_code: zod_1.z.string().trim().min(1).max(exports.ELIGIBILITY_REASON_CODE_MAX_LEN),
    unresolved_reason_code: optionalTrimmedText(exports.ELIGIBILITY_REASON_CODE_MAX_LEN),
    severity: vocabulary_1.eligibilityRuleSeveritySchema,
    is_enabled: zod_1.z.boolean().default(true),
    effective_from: isoDate.nullable().optional(),
    effective_to: isoDate.nullable().optional(),
    position: zod_1.z.number().int().min(0).optional(),
    conditions: zod_1.z
        .array(exports.eligibilityRuleConditionInputSchema)
        .refine((conds) => conds.some((c) => c.role === 'requirement'), {
        message: 'a rule must have at least one requirement condition',
    }),
})
    .strict();
//# sourceMappingURL=rule-schema.js.map